import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AdminLayout } from '../components/Layout/AdminLayout';
import { PageHeader, StatCard } from '../components/common';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  Button,
} from '@mui/material';
import { ArrowBack, Business, Receipt, Description, Block, CheckCircle, AccountBalanceWallet, Assignment, Person } from '@mui/icons-material';
import { colors } from '../theme/theme';
import { customerService } from '../services';
import { useSnackbar } from '../contexts/SnackbarContext';
import { useQueryClient } from '@tanstack/react-query';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`customer-tabpanel-${index}`}
      aria-labelledby={`customer-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

export const CustomerDetailsPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();
  const [currentTab, setCurrentTab] = useState(0);
  const [customerDetails, setCustomerDetails] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const fetchCustomerDetails = async () => {
      if (!id) return;

      try {
        setIsLoading(true);
        const response = await customerService.getCustomerDetails(parseInt(id));
        setCustomerDetails(response.data);
      } catch (error) {
        console.error('Failed to load customer details:', error);
        setError(true);
        showSnackbar('Failed to load customer details', 'error');
      } finally {
        setIsLoading(false);
      }
    };

    fetchCustomerDetails();
  }, [id, showSnackbar]);

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  const handleSuspend = async () => {
    if (!customerDetails?.customer) return;

    const action = customerDetails.customer.status === 'suspended' ? 'activate' : 'suspend';
    const confirmMessage = `Are you sure you want to ${action} this customer?`;

    if (!window.confirm(confirmMessage)) return;

    try {
      await customerService.toggleSuspend(customerDetails.customer.id);
      showSnackbar(`Customer ${action}d successfully`, 'success');

      // Refresh customer details
      const response = await customerService.getCustomerDetails(parseInt(id!));
      setCustomerDetails(response.data);
      queryClient.invalidateQueries({ queryKey: ['customers'] });
    } catch (error) {
      showSnackbar(`Failed to ${action} customer`, 'error');
    }
  };

  if (isLoading) {
    return (
      <AdminLayout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </AdminLayout>
    );
  }

  if (error || !customerDetails) {
    return (
      <AdminLayout>
        <Alert severity="error" sx={{ mb: 3 }}>
          Failed to load customer details. Please try again.
        </Alert>
        <Button variant="outlined" startIcon={<ArrowBack />} onClick={() => navigate(-1)}>
          Go Back
        </Button>
      </AdminLayout>
    );
  }

  const customer = customerDetails.customer;
  const isBusiness = customerDetails.is_business || customer?.user_type === 2;
  const assets = customerDetails.assets || [];
  const transactions = customerDetails.transactions || [];
  const serviceApplications = customerDetails.service_applications || [];
  const stats = customerDetails.stats || {};
  const transactionSummary = customerDetails.transaction_summary || {};

  return (
    <AdminLayout>
      <Box>
        <PageHeader
          title={isBusiness ? (customer.business_name || `${customer.first_name} ${customer.last_name}`) : `${customer.first_name} ${customer.last_name}`}
          subtitle={isBusiness ? 'Business Account' : (customer.email || 'No email')}
          breadcrumbs={[
            { label: 'Dashboard', path: '/dashboard' },
            { label: 'Customers', path: '/customers' },
            { label: 'Details' },
          ]}
          action={
            <Box display="flex" gap={2}>
              <Button
                variant="outlined"
                startIcon={<ArrowBack />}
                onClick={() => navigate(-1)}
              >
                Back
              </Button>
              <Button
                variant={customer.status === 'suspended' ? 'contained' : 'outlined'}
                color={customer.status === 'suspended' ? 'success' : 'warning'}
                startIcon={customer.status === 'suspended' ? <CheckCircle /> : <Block />}
                onClick={handleSuspend}
              >
                {customer.status === 'suspended' ? 'Activate' : 'Suspend'}
              </Button>
            </Box>
          }
        />

        {/* Business Info Card - only for business customers */}
        {isBusiness && (
          <Card
            sx={{
              mb: 3,
              p: 2.5,
              bgcolor: colors.primary[50] || '#eff6ff',
              border: `1px solid ${colors.primary.main}30`,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Business sx={{ color: colors.primary.main }} />
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 600, color: colors.primary.main }}
              >
                Business Information
              </Typography>
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: 2.5 }}>
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  Business Name
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mt: 0.5 }}>
                  {customer.business_name || 'N/A'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  KRA PIN
                </Typography>
                <Typography variant="body1" sx={{ mt: 0.5, fontFamily: 'monospace', fontWeight: 600 }}>
                  {customer.kra_pin || 'N/A'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  Registration Number
                </Typography>
                <Typography variant="body1" sx={{ mt: 0.5, fontFamily: 'monospace' }}>
                  {customer.registration_number || 'Not Registered'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  Status
                </Typography>
                <Box sx={{ mt: 0.5 }}>
                  <Chip
                    label={customer.status || 'Active'}
                    size="small"
                    color={customer.status === 'suspended' ? 'warning' : 'success'}
                    sx={{ textTransform: 'capitalize' }}
                  />
                </Box>
              </Box>
            </Box>
          </Card>
        )}

        {/* Owner/User Info Card */}
        <Card
          sx={{
            mb: 3,
            p: 2.5,
            bgcolor: colors.grey[50],
            border: `1px solid ${colors.grey[200]}`,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Person sx={{ color: colors.primary.main }} />
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 600, color: colors.primary.main }}
            >
              {isBusiness ? 'Owner / Custodian' : 'Personal Information'}
            </Typography>
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: 2.5 }}>
            <Box>
              <Typography variant="caption" color="text.secondary" display="block">
                Full Name
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 600,
                  mt: 0.5,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {customer.first_name} {customer.middle_name || ''} {customer.last_name}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" display="block">
                Email
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  mt: 0.5,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
                title={customer.email || 'N/A'}
              >
                {customer.email || 'N/A'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" display="block">
                Phone
              </Typography>
              <Typography variant="body1" sx={{ mt: 0.5 }}>
                {customer.phone || 'N/A'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" display="block">
                ID Number
              </Typography>
              <Typography variant="body1" sx={{ mt: 0.5 }}>
                {customer.id_number || 'N/A'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" display="block">
                Address
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  mt: 0.5,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
                title={customer.profile?.address || 'N/A'}
              >
                {customer.profile?.address || 'N/A'}
              </Typography>
            </Box>
            {!isBusiness && (
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  Status
                </Typography>
                <Box sx={{ mt: 0.5 }}>
                  <Chip
                    label={customer.status || 'Active'}
                    size="small"
                    color={customer.status === 'suspended' ? 'warning' : 'success'}
                    sx={{ textTransform: 'capitalize' }}
                  />
                </Box>
              </Box>
            )}
          </Box>
        </Card>

        {/* Stats Cards */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 3, mb: 3 }}>
          <StatCard
            title="Total Assets"
            value={stats.total_assets || 0}
            icon={<Business />}
            color={colors.primary.main}
          />
          <StatCard
            title="Total Spent"
            value={`KES ${(stats.total_spent || 0).toLocaleString()}`}
            icon={<AccountBalanceWallet />}
            color={colors.success.main}
          />
          <StatCard
            title="Transactions"
            value={stats.total_transactions || 0}
            icon={<Receipt />}
            color={colors.info.main}
          />
          <StatCard
            title="Applications"
            value={stats.total_applications || 0}
            icon={<Assignment />}
            color={colors.secondary.main}
          />
        </Box>

        {/* Tabs */}
        <Card>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={currentTab} onChange={handleTabChange}>
              <Tab icon={<Business />} label="Assets & Businesses" iconPosition="start" />
              <Tab icon={<Receipt />} label="Transactions" iconPosition="start" />
              <Tab icon={<Description />} label="Applications" iconPosition="start" />
            </Tabs>
          </Box>

          {/* Assets Tab */}
          <TabPanel value={currentTab} index={0}>
            {assets.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                No assets found
              </Typography>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Asset Name</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Category</TableCell>
                      <TableCell>Reference</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {assets.map((asset: any) => (
                      <TableRow key={asset.id}>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            {asset.asset_name}
                          </Typography>
                        </TableCell>
                        <TableCell>{asset.asset_type}</TableCell>
                        <TableCell>{asset.businessCategory?.name || 'N/A'}</TableCell>
                        <TableCell>{asset.asset_reference || 'N/A'}</TableCell>
                        <TableCell>
                          <Chip
                            label={asset.status || 'Active'}
                            size="small"
                            color="success"
                            sx={{ textTransform: 'capitalize' }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </TabPanel>

          {/* Transactions Tab */}
          <TabPanel value={currentTab} index={1}>
            {/* Transaction Summary */}
            <Card sx={{ mb: 2, p: 2, bgcolor: colors.grey[50] }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 2 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Total Amount
                  </Typography>
                  <Typography variant="h6" color={colors.primary.main}>
                    KES {(transactionSummary.total_amount || 0).toLocaleString()}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Paid
                  </Typography>
                  <Typography variant="h6" color={colors.success.main}>
                    KES {(transactionSummary.paid_amount || 0).toLocaleString()}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Pending
                  </Typography>
                  <Typography variant="h6" color={colors.warning.main}>
                    KES {(transactionSummary.pending_amount || 0).toLocaleString()}
                  </Typography>
                </Box>
              </Box>
            </Card>

            {transactions.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                No transactions found
              </Typography>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Service</TableCell>
                      <TableCell>Touch Point</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {transactions.map((transaction: any) => (
                      <TableRow key={transaction.id}>
                        <TableCell>
                          {new Date(transaction.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell>{transaction.charge?.service?.name || 'N/A'}</TableCell>
                        <TableCell>{transaction.touchPoint?.name || 'N/A'}</TableCell>
                        <TableCell align="right">
                          KES {transaction.amount?.toLocaleString()}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={transaction.status}
                            size="small"
                            color={
                              ['completed', 'success', 'successful'].includes(transaction.status)
                                ? 'success'
                                : transaction.status === 'pending'
                                ? 'warning'
                                : 'error'
                            }
                            sx={{ textTransform: 'capitalize' }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </TabPanel>

          {/* Applications Tab */}
          <TabPanel value={currentTab} index={2}>
            {serviceApplications.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                No applications found
              </Typography>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Service</TableCell>
                      <TableCell>Reference</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {serviceApplications.map((application: any) => (
                      <TableRow key={application.id}>
                        <TableCell>
                          {new Date(application.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell>{application.service?.name || 'N/A'}</TableCell>
                        <TableCell>{application.submission_reference || 'N/A'}</TableCell>
                        <TableCell>
                          <Chip
                            label={application.status}
                            size="small"
                            color={
                              application.status === 'approved'
                                ? 'success'
                                : application.status === 'rejected'
                                ? 'error'
                                : 'warning'
                            }
                            sx={{ textTransform: 'capitalize' }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </TabPanel>
        </Card>
      </Box>
    </AdminLayout>
  );
};
